import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  CircularProgress,
  Box,
  Grid,
  IconButton,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import {
  getTripolaPlanByIdAction,
  createTripolaPlanAction,
  updateTripolaPlanAction,
} from "../../Actions/TripolaPlanActions";
import CloseIcon from "@mui/icons-material/Close";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const { VALIDATION } = FRONTEND_MESSAGES;

const validationSchema = Yup.object({
  plan_name: Yup.string()
    .trim()
    .matches(/^[a-zA-Z0-9\s&-]+$/, VALIDATION.INVALID_FORMAT("Plan name"))
    .max(50, VALIDATION.MAX_LENGTH("Plan name", 50))
    .required(VALIDATION.REQUIRED_FIELD("Plan name")),

  plan_description: Yup.string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Description")),

  targeted_users: Yup.string()
    .trim()
    .max(500, VALIDATION.MAX_LENGTH("Targeted Users", 500))
    .required(VALIDATION.REQUIRED_FIELD("Targeted Users")),

  monthly_price: Yup.number()
    .typeError(VALIDATION.NUMERIC_ONLY("Monthly price"))
    .positive(VALIDATION.POSITIVE_ONLY("Monthly price"))
    .max(999999999, VALIDATION.MAX_VALUE("Monthly price", 999999999))
    .required(VALIDATION.REQUIRED_FIELD("Monthly price")),

  yearly_price: Yup.number()
    .typeError(VALIDATION.NUMERIC_ONLY("Yearly price"))
    .positive(VALIDATION.POSITIVE_ONLY("Yearly price"))
    .max(999999999, VALIDATION.MAX_VALUE("Yearly price", 999999999))
    .required(VALIDATION.REQUIRED_FIELD("Yearly price")),

  is_active: Yup.boolean(),

  // TOUR MANAGEMENT VALIDATION
  tour_management: Yup.boolean().test(
    "tour-sub-feature-required",
    "Please select at least one Tour Management sub-feature",
    function (value) {
      if (!value) return true;

      const {
        tour_cancellation_policy_management,
        tour_additional_policies,
        tour_package_creation,
        tour_booking_questions,
        tour_seo_settings,
        tour_product,
        tour_accommodation,
      } = this.parent;

      return (
        tour_cancellation_policy_management ||
        tour_additional_policies ||
        tour_package_creation ||
        tour_booking_questions ||
        tour_seo_settings ||
        tour_product ||
        tour_accommodation
      );
    }
  ),

  // EVENT MANAGEMENT VALIDATION
  event_management: Yup.boolean().test(
    "event-sub-feature-required",
    "Please select at least one Event Management sub-feature",
    function (value) {
      if (!value) return true;

      const {
        cancellation_policy_management,
        additional_policies,
        package_creation,
        booking_questions,
        seo_settings,
        event_product,
        event_accommodation,
      } = this.parent;

      return (
        cancellation_policy_management ||
        additional_policies ||
        package_creation ||
        booking_questions ||
        seo_settings ||
        event_product ||
        event_accommodation
      );
    }
  ),

  product_management: Yup.boolean().test(
    "product-sub-feature-required",
    "Please select at least one Product Management sub-feature",
    function (value) {
      if (!value) return true;

      const {
        product_add_on,
        self_purchase,
        product_cancellation_policy,
        product_additional_policy,
        product_seo_optimization,
      } = this.parent;

      return (
        product_add_on ||
        self_purchase ||
        product_cancellation_policy ||
        product_additional_policy ||
        product_seo_optimization
      );
    }
  ),
});

// Helper: Compare old vs new values (Deep comparison)
const getChangedPlanFields = (oldData, newData) => {
  const changes = {};
  if (!oldData) return changes;

  Object.keys(newData).forEach((key) => {
    const oldVal = oldData[key];
    const newVal = newData[key];

    const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
    const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

    if (oldIsEmpty && newIsEmpty) return;

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = {
        old: oldVal,
        new: newVal,
      };
    }
  });

  return changes;
};

const AddEditTripolaPlan = ({
  open,
  handleClose,
  id,
  refetchPlanList = () => { },
  handleToast = () => { },
  changePortalFilter,
}) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [planData, setPlanData] = useState(null);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [changedFields, setChangedFields] = useState({});

  const formik = useFormik({
    // initialValues: {
    //   plan_name: "",
    //   plan_description: "",
    //   targeted_users: "",
    //   tour_management: false,
    //   event_management: false,
    //   ticket_management: false,
    //   vehicle_management: false,
    //   product_management: false,
    //   coupon_management: false,
    //   accommodation: false,
    //   monthly_price: "",
    //   yearly_price: "",
    //   is_active: true,
    // },
    initialValues: {
      plan_name: "",
      plan_description: "",
      targeted_users: "",
      tour_management: false,
      event_management: false,
      ticket_management: false,
      product_management: false,
      coupon_management: false,
      accommodation: false,
      cancellation_policy_management: false,
      additional_policies: false,
      package_creation: false,
      booking_questions: false,
      seo_settings: false,
      event_product: false,
      event_accommodation: false,
      fixed_packages: false,
      flexible_packages: false,
      sell_product_directly: false,
      add_on_tour_event: false,
      event_accommodation_add_on: false,
      tour_cancellation_policy_management: false,
      tour_additional_policies: false,
      tour_package_creation: false,
      tour_fixed_packages: false,
      tour_flexible_packages: false,
      tour_vehicle_management: false,
      tour_booking_questions: false,
      tour_seo_settings: false,
      tour_product: false,
      tour_sell_product_directly: false,
      tour_add_on_tour_event: false,
      tour_accommodation: false,
      tour_accommodation_add_on: false,
      product_add_on: false,
      self_purchase: false,
      product_cancellation_policy: false,
      product_additional_policy: false,
      product_seo_optimization: false,
      gift_card: false,
      voucher: false,
      coupon: false,
      accommodation_add_on: false,
      ticket_types: false,
      ticket_cancellation_policy: false,
      // ticket_additional_policy: false,
      monthly_price: "",
      yearly_price: "",
      is_active: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (
        values.tour_management &&
        !(
          values.tour_cancellation_policy_management ||
          values.tour_additional_policies ||
          values.tour_package_creation ||
          values.tour_booking_questions ||
          values.tour_seo_settings ||
          values.tour_product ||
          values.tour_accommodation
        )
      ) {
        handleToast(
          "error",
          "Please select at least one Tour Management sub-feature"
        );
        return;
      }

      if (
        values.event_management &&
        !(
          values.cancellation_policy_management ||
          values.additional_policies ||
          values.package_creation ||
          values.booking_questions ||
          values.seo_settings ||
          values.event_product ||
          values.event_accommodation
        )
      ) {
        handleToast(
          "error",
          "Please select at least one Event Management sub-feature"
        );
        return;
      }
      const payload = {
        // ...values,
        // plan_name: values.plan_name?.trim(),
        // plan_description: values.plan_description?.trim(),
        // targeted_users: values.targeted_users?.trim(),
        plan_name: values.plan_name?.trim(),
        plan_description: values.plan_description?.trim(),
        targeted_users: values.targeted_users?.trim(),

        features: {
          tour_management: {
            enabled: values.tour_management,
            cancellation_policy_management:
              values.tour_cancellation_policy_management,
            additional_policies:
              values.tour_additional_policies,

            package_creation: {
              enabled: values.tour_package_creation,
              fixed_packages:
                values.tour_fixed_packages,
              flexible_packages:
                values.tour_flexible_packages,
              vehicle_management:
                values.tour_vehicle_management,
            },

            booking_questions:
              values.tour_booking_questions,

            seo_settings:
              values.tour_seo_settings,

            product: {
              enabled: values.tour_product,
              sell_product_directly:
                values.tour_sell_product_directly,
              add_on_tour_event:
                values.tour_add_on_tour_event,
            },

            accommodation: {
              enabled: values.tour_accommodation,
              add_on:
                values.tour_accommodation_add_on,
            },
          },

          event_management: {
            enabled: values.event_management,
            cancellation_policy_management:
              values.cancellation_policy_management,
            additional_policies:
              values.additional_policies,

            package_creation: {
              enabled: values.package_creation,
              fixed_packages:
                values.fixed_packages,
              flexible_packages:
                values.flexible_packages,
            },

            booking_questions:
              values.booking_questions,

            seo_settings:
              values.seo_settings,

            product: {
              enabled: values.event_product,
              sell_product_directly:
                values.sell_product_directly,
              add_on_tour_event:
                values.add_on_tour_event,
            },

            accommodation: {
              enabled: values.event_accommodation,
              add_on:
                values.event_accommodation_add_on,
            },
          },

          ticket_management: {
            enabled: values.ticket_management,

            ticket_types: values.ticket_types,

            cancellation_policy:
              values.ticket_cancellation_policy,

            // additional_policy:
            //   values.ticket_additional_policy,
          },

          product_management: {
            enabled: values.product_management,
            product_add_on: values.product_add_on,
            self_purchase: values.self_purchase,
            cancellation_policy: values.product_cancellation_policy,
            additional_policy: values.product_additional_policy,
            seo_optimization: values.product_seo_optimization,
          },

          coupon_management: {
            enabled: values.coupon_management,
            gift_card:
              values.gift_card,
            voucher:
              values.voucher,
            coupon:
              values.coupon,
          },

          accommodation: {
            enabled: values.accommodation,
            add_on:
              values.accommodation_add_on,
          },
        },

        monthly_price: values.monthly_price,
        yearly_price: values.yearly_price,
        is_active: values.is_active,
      };

      if (id) {
        payload.changedFields = JSON.stringify(Object.keys(changedFields));
        payload.comparisonData = JSON.stringify(changedFields);
      }
      setLoading(true);
      try {
        if (id) {
          const response = await updateTripolaPlanAction(id, payload);
          const { message } = response || {};
          handleToast("success", message || "Plan updated successfully");
        } else {
          const response = await createTripolaPlanAction(payload);
          const { message } = response || {};
          changePortalFilter("tripola");
          handleToast("success", message || "Plan created successfully");
        }
        handleClose();
      } catch (err) {
        const message =
          err?.message ||
          (id
            ? "Something went wrong while updating plan"
            : "Something went wrong while adding plan");

        handleToast("error", message);
        console.error("❌ Error saving plan:", err);
      } finally {
        setLoading(false);
        refetchPlanList();
      }
    },
  });

  useEffect(() => {
    const fetchPlan = async () => {
      if (!id) return;
      setFetching(true);
      try {
        const response = await getTripolaPlanByIdAction(id);
        const data = response?.data?.data || {};
        const features = data.features || {};

        setPlanData(data);

        // formik.resetForm({
        //   values: {
        //     plan_name: data.plan_name || "",
        //     plan_description: data.plan_description || "",
        //     targeted_users: data.targeted_users || "",
        //     tour_management: data.tour_management ?? false,
        //     event_management: data.event_management ?? false,
        //     ticket_management: data.ticket_management ?? false,
        //     vehicle_management: data.vehicle_management ?? false,
        //     product_management: data.product_management ?? false,
        //     coupon_management: data.coupon_management ?? false,
        //     accommodation: data.accommodation ?? false,
        //     monthly_price: data.monthly_price ?? "",
        //     yearly_price: data.yearly_price ?? "",
        //     is_active: data.is_active ?? true,
        //   },
        // });
        const initialValuesToSet = {
          ...formik.initialValues,

          plan_name: data.plan_name || "",
          plan_description: data.plan_description || "",
          targeted_users: data.targeted_users || "",

          // Tour
          tour_management: features.tour_management?.enabled ?? false,
          tour_cancellation_policy_management:
            features.tour_management?.cancellation_policy_management ?? false,
          tour_additional_policies:
            features.tour_management?.additional_policies ?? false,
          tour_package_creation:
            features.tour_management?.package_creation?.enabled ?? false,
          tour_fixed_packages:
            features.tour_management?.package_creation?.fixed_packages ?? false,
          tour_flexible_packages:
            features.tour_management?.package_creation?.flexible_packages ?? false,
          tour_vehicle_management:
            features.tour_management?.package_creation?.vehicle_management ?? false,
          tour_booking_questions:
            features.tour_management?.booking_questions ?? false,
          tour_seo_settings:
            features.tour_management?.seo_settings ?? false,
          tour_product:
            features.tour_management?.product?.enabled ?? false,
          tour_sell_product_directly:
            features.tour_management?.product?.sell_product_directly ?? false,
          tour_add_on_tour_event:
            features.tour_management?.product?.add_on_tour_event ?? false,
          tour_accommodation:
            features.tour_management?.accommodation?.enabled ?? false,
          tour_accommodation_add_on:
            features.tour_management?.accommodation?.add_on ?? false,

          // Event
          event_management:
            features.event_management?.enabled ?? false,
          cancellation_policy_management:
            features.event_management?.cancellation_policy_management ?? false,
          additional_policies:
            features.event_management?.additional_policies ?? false,
          package_creation:
            features.event_management?.package_creation?.enabled ?? false,
          fixed_packages:
            features.event_management?.package_creation?.fixed_packages ?? false,
          flexible_packages:
            features.event_management?.package_creation?.flexible_packages ?? false,
          booking_questions:
            features.event_management?.booking_questions ?? false,
          seo_settings:
            features.event_management?.seo_settings ?? false,
          event_product:
            features.event_management?.product?.enabled ?? false,
          sell_product_directly:
            features.event_management?.product?.sell_product_directly ?? false,
          add_on_tour_event:
            features.event_management?.product?.add_on_tour_event ?? false,
          event_accommodation:
            features.event_management?.accommodation?.enabled ?? false,
          event_accommodation_add_on:
            features.event_management?.accommodation?.add_on ?? false,

          // Ticket
          ticket_management:
            features.ticket_management?.enabled ?? false,

          ticket_types:
            features.ticket_management?.ticket_types ?? false,

          ticket_cancellation_policy:
            features.ticket_management?.cancellation_policy ?? false,

          // ticket_additional_policy:
          //   features.ticket_management?.additional_policy ?? false,

          // Product
          product_management:
            features.product_management?.enabled ?? false,
          product_add_on:
            features.product_management?.product_add_on ?? false,
          self_purchase:
            features.product_management?.self_purchase ?? false,
          product_cancellation_policy:
            features.product_management?.cancellation_policy ?? false,

          product_additional_policy:
            features.product_management?.additional_policy ?? false,

          product_seo_optimization:
            features.product_management?.seo_optimization ?? false,

          // Voucher
          coupon_management:
            features.coupon_management?.enabled ?? false,
          gift_card:
            features.coupon_management?.gift_card ?? false,
          voucher:
            features.coupon_management?.voucher ?? false,
          coupon:
            features.coupon_management?.coupon ?? false,

          // Accommodation
          accommodation:
            features.accommodation?.enabled ?? false,
          accommodation_add_on:
            features.accommodation?.add_on ?? false,

          monthly_price: data.monthly_price ?? "",
          yearly_price: data.yearly_price ?? "",
          is_active: data.is_active ?? true,
        };

        formik.resetForm({
          values: initialValuesToSet,
        });
        setInitialFormValues(initialValuesToSet);
      } catch (err) {
        console.error("❌ Error fetching plan:", err);
      } finally {
        setFetching(false);
      }
    };
    fetchPlan();
  }, [id]);

  // Track changed fields for audit/update logging
  useEffect(() => {
    if (!id || !initialFormValues) return;

    const changed = getChangedPlanFields(initialFormValues, formik.values);
    setChangedFields(changed);
  }, [formik.values, initialFormValues, id]);


  const handleTextChange = (e) => {
    formik.handleChange(e);
    formik.setFieldTouched(e.target.name, true, false);
  };



  const handleNumberChange = (e) => {
    const value = e.target.value.replace(/\s/g, "");
    formik.setFieldValue(e.target.name, value);
    formik.setFieldTouched(e.target.name, true, false);
  };

  const SectionHeader = ({ title }) => (
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: "primary.main", mb: 2 }}
    >
      {title}
    </Typography>
  );

  const planFeatures = [
    {
      key: "tour_management",
      label: "Tour Management",
      description: "Users can create and manage tour listings.",
      subcategories: [
        {
          key: "tour_cancellation_policy_management",
          label: "Cancellation Policy Management",
          description: "Configure refund and cancellation rules for tours.",
        },
        {
          key: "tour_additional_policies",
          label: "Additional Policies",
          description: "Add custom text policies and upload policy documents.",
        },
        {
          key: "tour_package_creation",
          label: "Package Creation",
          description: "Create and manage different tour package types.",
          subcategories: [
            {
              key: "tour_fixed_packages",
              label: "Fixed Packages",
              description:
                "Fixed pricing, fixed participant count, and slot management.",
            },
            {
              key: "tour_flexible_packages",
              label: "Flexible Packages",
              description:
                "Adult/child pricing with min/max booking limits.",
            },
            {
              key: "tour_vehicle_management",
              label: "Vehicle Management",
              description:
                "Manage transport options associated with tour packages.",
            },
          ],
        },
        {
          key: "tour_booking_questions",
          label: "Booking Questions",
          description:
            "Custom attendee questions like emergency contact and medical info.",
        },
        {
          key: "tour_seo_settings",
          label: "SEO Settings",
          description:
            "Configure meta title, meta description, keywords, and URL slug.",
        },
        {
          key: "tour_product",
          label: "Product",
          description: "Attach products to tours.",
          subcategories: [
            {
              key: "tour_sell_product_directly",
              label: "Sell Product Directly",
              description: "Sell standalone products directly to customers.",
            },
            {
              key: "tour_add_on_tour_event",
              label: "As Add On Tour/Event",
              description:
                "Offer products as add-ons during tour or event booking.",
            },
          ],
        },
        {
          key: "tour_accommodation",
          label: "Accommodation",
          description: "Attach accommodation options to tours.",
          subcategories: [
            {
              key: "tour_accommodation_add_on",
              label: "Add On",
              description:
                "Offer accommodation as an optional add-on during booking.",
            },
          ],
        },
      ],
    },
    {
      key: "event_management",
      label: "Event Management",
      description: "Users can create and manage event listings with booking, policies, packages, products, accommodation, and SEO configuration.",
      subcategories: [
        {
          key: "cancellation_policy_management",
          label: "Cancellation Policy Management",
          description: "Configure refund and cancellation rules for event bookings.",
        },
        {
          key: "additional_policies",
          label: "Additional Policies",
          description: "Add custom text policies and upload policy documents.",
        },
        {
          key: "package_creation",
          label: "Package Creation",
          description: "Create fixed and flexible event packages.",
          subcategories: [
            {
              key: "fixed_packages",
              label: "Fixed Packages",
              description: "Fixed pricing and fixed participant count.",
            },
            {
              key: "flexible_packages",
              label: "Flexible Packages",
              description: "Flexible attendee and pricing options.",
            },
          ],
        },
        {
          key: "booking_questions",
          label: "Booking Questions",
          description: "Collect custom attendee information during booking.",
        },
        {
          key: "seo_settings",
          label: "SEO Settings",
          description: "Manage SEO title, meta description, keywords, and URL.",
        },
        {
          key: "event_product",
          label: "Product",
          description: "Attach products to event bookings.",
          subcategories: [
            {
              key: "sell_product_directly",
              label: "Sell Product Directly",
              description: "Sell standalone products directly.",
            },
            {
              key: "add_on_tour_event",
              label: "As Add On Tour/Event",
              description: "Offer products as add-ons during booking.",
            },
          ],
        },
        {
          key: "event_accommodation",
          label: "Accommodation",
          description: "Attach accommodation options to event bookings.",
          subcategories: [
            {
              key: "event_accommodation_add_on",
              label: "Add On",
              description: "Offer accommodation as optional add-on.",
            },
          ],
        },
      ],
    },
    {
      key: "ticket_management",
      label: "Ticket Management",
      description:
        "Users can create and manage ticket bookings with pass-based ticketing, cancellation policies, and additional booking policies.",
      subcategories: [
        {
          key: "ticket_types",
          label: "Ticket Pass",
          description:
            "Create validity-based travel passes like daily, weekly, and monthly passes.",
        },
        {
          key: "ticket_cancellation_policy",
          label: "Cancellation Policy",
          description:
            "Configure refund and cancellation rules for ticket bookings.",
        },
        // {
        //   key: "ticket_additional_policy",
        //   label: "Additional Policy",
        //   description:
        //     "Add transport rules, booking guidelines, and policy documents.",
        // },
      ],
    },
    {
      key: "product_management",
      label: "Product Management",
      description:
        "Users can create and sell products directly from the platform.",
      subcategories: [
        {
          key: "product_add_on",
          label: "Product Add Ons",
          description:
            "Manage products with inventory, variants, and pricing.",
        },
        {
          key: "self_purchase",
          label: "Product Self Purchase",
          description:
            "Allow customers to purchase and add products.",
        },
        {
          key: "product_cancellation_policy",
          label: "Cancellation Policy",
          description:
            "Configure refund and cancellation rules for product purchases.",
        },
        {
          key: "product_additional_policy",
          label: "Additional Policy",
          description:
            "Add transport rules, booking guidelines, and policy documents.",
        },
        {
          key: "product_seo_optimization",
          label: "SEO Optimization",
          description:
            "Manage SEO title, meta description, keywords, and URL slug for products.",
        },
      ],
    },
    {
      key: "coupon_management",
      label: "Voucher / Gift Card / Coupon",
      description:
        "Users can create, sell, redeem, and manage vouchers, gift cards, and discount coupons.",
      subcategories: [
        {
          key: "gift_card",
          label: "Gift Cards / Vouchers",
          description:
            "Create and sell digital gift cards and vouchers."
        },
        {
          key: "voucher",
          label: "Voucher",
          description:
            "Control where vouchers can be used.",
        },
        {
          key: "coupon",
          label: "Coupon",
          description:
            "Manage promotional coupons and discounts.",
        },
      ],
    },
    {
      key: "accommodation",
      label: "Accommodation",
      description: "Users can manage different accommodation types",
      subcategories: [
        {
          key: "accommodation_add_on",
          label: "Accommodation Add Ons",
          description:
            "Users can manage different accommodation types.",
        }
      ],
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        className="text-bg-primary"
        sx={{
          py: 2.5,
          px: 3,
          fontSize: "1.25rem",
          fontWeight: 600,
          position: "relative",
        }}
      >
        {id ? "Update Tripola Plan" : "Create New Tripola Plan"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#fff",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {fetching ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={400}
        >
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <DialogContent sx={{ px: 3, py: 3 }}>
            {/* Plan Information Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <SectionHeader title="Plan Information" />
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Plan Name<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter plan name"
                    name="plan_name"
                    size="small"
                    fullWidth
                    disabled={id}
                    value={formik.values.plan_name}
                    onChange={handleTextChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.plan_name &&
                      Boolean(formik.errors.plan_name)
                    }
                    helperText={
                      formik.touched.plan_name && formik.errors.plan_name
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Plan Description<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter description"
                    name="plan_description"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    value={formik.values.plan_description}
                    onChange={handleTextChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.plan_description &&
                      Boolean(formik.errors.plan_description)
                    }
                    helperText={
                      formik.touched.plan_description &&
                      formik.errors.plan_description
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Targeted Users<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter targeted users description"
                    name="targeted_users"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={formik.values.targeted_users}
                    onChange={handleTextChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.targeted_users &&
                      Boolean(formik.errors.targeted_users)
                    }
                    helperText={
                      formik.touched.targeted_users &&
                      formik.errors.targeted_users
                    }
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Plan Status Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <SectionHeader title="Plan Status" />
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.is_active}
                    onChange={(e) =>
                      formik.setFieldValue("is_active", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {formik.values.is_active ? "Active" : "Inactive"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formik.values.is_active
                        ? "This plan is available for users to subscribe"
                        : "This plan is hidden from users"}
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            {/* Plan Features Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <SectionHeader title="Plan Features" />
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "rgba(25, 118, 210, 0.04)",
                  border: "1px solid",
                  borderColor: "rgba(25, 118, 210, 0.12)",
                }}
              >
                <Grid container spacing={2}>
                  {planFeatures.map((feature) => (
                    <Grid item xs={12} key={feature.key}>
                      {feature.subcategories?.length ? (
                        <Accordion
                          sx={{
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "grey.300",
                            boxShadow: "none",
                          }}
                        >
                          {/* Main Feature */}
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              flexDirection: "row-reverse",
                              "& .MuiAccordionSummary-expandIconWrapper": {
                                marginRight: "12px",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography fontWeight={600}>
                                {feature.label}
                              </Typography>

                              <Checkbox
                                checked={formik.values[feature.key]}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  formik.setFieldValue(feature.key, checked);
                                  formik.setFieldTouched(feature.key, true, false);

                                  // --- NEW: PARENT-TO-CHILD SYNC (When root Accommodation is toggled) ---
                                  if (feature.key === "accommodation") {
                                    if (formik.values.tour_management) {
                                      formik.setFieldValue("tour_accommodation", checked);
                                    }
                                    if (formik.values.event_management) {
                                      formik.setFieldValue("event_accommodation", checked);
                                    }

                                    // Automatically clean up all child add-ons if root accommodation is turned off
                                    if (!checked) {
                                      formik.setFieldValue("accommodation_add_on", false);
                                      formik.setFieldValue("tour_accommodation_add_on", false);
                                      formik.setFieldValue("event_accommodation_add_on", false);
                                    }
                                  }

                                  if (feature.key === "product_management") {
                                    if (formik.values.tour_management) {
                                      formik.setFieldValue("tour_product", checked);
                                    }

                                    if (formik.values.event_management) {
                                      formik.setFieldValue("event_product", checked);
                                    }

                                    if (!checked) {
                                      formik.setFieldValue("product_add_on", false);
                                      formik.setFieldValue("self_purchase", false);

                                      formik.setFieldValue("tour_sell_product_directly", false);
                                      formik.setFieldValue("sell_product_directly", false);

                                      formik.setFieldValue("tour_add_on_tour_event", false);
                                      formik.setFieldValue("add_on_tour_event", false);
                                    }
                                  }

                                  // Your existing uncheck cleanup logic
                                  if (!checked && feature.subcategories) {
                                    feature.subcategories.forEach((sub) => {
                                      formik.setFieldValue(sub.key, false);

                                      sub.subcategories?.forEach((nested) => {
                                        formik.setFieldValue(nested.key, false);
                                        nested.subcategories?.forEach((deepNested) => {
                                          formik.setFieldValue(deepNested.key, false);
                                        });
                                      });

                                      // Handle root accommodation sync
                                      if (sub.key === "tour_accommodation" || sub.key === "event_accommodation") {
                                        const otherAccommodationEnabled =
                                          (sub.key === "tour_accommodation" && formik.values.event_accommodation) ||
                                          (sub.key === "event_accommodation" && formik.values.tour_accommodation);

                                        if (!otherAccommodationEnabled) {
                                          formik.setFieldValue("accommodation", false);
                                        }
                                      }

                                      // Handle root accommodation add-on sync
                                      if (sub.subcategories?.some(
                                        (nested) => nested.key === "tour_accommodation_add_on" || nested.key === "event_accommodation_add_on"
                                      )) {
                                        const otherAddOnEnabled =
                                          feature.key === "tour_management"
                                            ? formik.values.event_accommodation_add_on
                                            : formik.values.tour_accommodation_add_on;

                                        if (!otherAddOnEnabled) {
                                          formik.setFieldValue("accommodation_add_on", false);
                                        }
                                      }
                                    });
                                  }
                                  if (
                                    !checked &&
                                    (feature.key === "tour_management" ||
                                      feature.key === "event_management")
                                  ) {
                                    const hasAnyProduct =
                                      feature.key === "tour_management"
                                        ? formik.values.event_product
                                        : formik.values.tour_product;

                                    if (!hasAnyProduct) {
                                      formik.setFieldValue("product_management", false);

                                      formik.setFieldValue("product_add_on", false);
                                      formik.setFieldValue("self_purchase", false);

                                      formik.setFieldValue(
                                        "tour_sell_product_directly",
                                        false
                                      );
                                      formik.setFieldValue(
                                        "sell_product_directly",
                                        false
                                      );

                                      formik.setFieldValue(
                                        "tour_add_on_tour_event",
                                        false
                                      );
                                      formik.setFieldValue(
                                        "add_on_tour_event",
                                        false
                                      );
                                    }

                                    const hasAnyAccommodation =
                                      feature.key === "tour_management"
                                        ? formik.values.event_accommodation
                                        : formik.values.tour_accommodation;

                                    if (!hasAnyAccommodation) {
                                      formik.setFieldValue("accommodation", false);

                                      formik.setFieldValue(
                                        "accommodation_add_on",
                                        false
                                      );

                                      formik.setFieldValue(
                                        "tour_accommodation_add_on",
                                        false
                                      );

                                      formik.setFieldValue(
                                        "event_accommodation_add_on",
                                        false
                                      );
                                    }
                                  }
                                }}
                              />
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails>
                            {feature.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                {feature.description}
                              </Typography>
                            )}

                            {formik.values[feature.key] && feature.subcategories?.length > 0 && (
                              <Grid container spacing={2} sx={{ mt: 2 }}>
                                {feature.subcategories.map((sub) => (
                                  <Grid item xs={12} sm={6} key={sub.key}>
                                    <Box
                                      sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        bgcolor: "grey.50",
                                        height: "100%",
                                      }}
                                    >
                                      {/* Subcategory */}
                                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <Typography variant="body2" fontWeight={500}>
                                          {sub.label}
                                        </Typography>

                                        <Checkbox
                                          checked={formik.values[sub.key]}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            formik.setFieldValue(sub.key, checked);

                                            // --- NEW: PARENT-TO-CHILD SYNC (When root Accommodation Add-on is toggled) ---
                                            if (sub.key === "accommodation_add_on") {
                                              if (formik.values.tour_management && formik.values.tour_accommodation) {
                                                formik.setFieldValue("tour_accommodation_add_on", checked);
                                              }
                                              if (formik.values.event_management && formik.values.event_accommodation) {
                                                formik.setFieldValue("event_accommodation_add_on", checked);
                                              }
                                            }
                                            if (sub.key === "self_purchase") {
                                              if (!checked) {
                                                formik.setFieldValue(
                                                  "tour_sell_product_directly",
                                                  false
                                                );

                                                formik.setFieldValue(
                                                  "sell_product_directly",
                                                  false
                                                );
                                              }
                                            }

                                            if (sub.key === "product_add_on") {
                                              if (!checked) {
                                                formik.setFieldValue(
                                                  "tour_add_on_tour_event",
                                                  false
                                                );

                                                formik.setFieldValue(
                                                  "add_on_tour_event",
                                                  false
                                                );
                                              }
                                            }
                                            if (sub.key === "self_purchase" && checked) {
                                              if (
                                                formik.values.tour_management &&
                                                formik.values.tour_product
                                              ) {
                                                formik.setFieldValue(
                                                  "tour_sell_product_directly",
                                                  true
                                                );
                                              }

                                              if (
                                                formik.values.event_management &&
                                                formik.values.event_product
                                              ) {
                                                formik.setFieldValue(
                                                  "sell_product_directly",
                                                  true
                                                );
                                              }
                                            }

                                            if (sub.key === "product_add_on" && checked) {
                                              if (
                                                formik.values.tour_management &&
                                                formik.values.tour_product
                                              ) {
                                                formik.setFieldValue(
                                                  "tour_add_on_tour_event",
                                                  true
                                                );
                                              }

                                              if (
                                                formik.values.event_management &&
                                                formik.values.event_product
                                              ) {
                                                formik.setFieldValue(
                                                  "add_on_tour_event",
                                                  true
                                                );
                                              }
                                            }
                                            // AUTO SELECT ROOT ACCOMMODATION
                                            if ((sub.key === "tour_accommodation" || sub.key === "event_accommodation") && checked) {
                                              formik.setFieldValue("accommodation", true);
                                            }
                                            if (
                                              (sub.key === "tour_product" ||
                                                sub.key === "event_product") &&
                                              checked
                                            ) {
                                              formik.setFieldValue("product_management", true);
                                            }

                                            // If subcategory unchecked → uncheck nested subcategories
                                            if (!checked && sub.subcategories) {
                                              sub.subcategories.forEach((nested) => {
                                                formik.setFieldValue(nested.key, false);
                                              });
                                            }


                                            // AUTO UNCHECK ROOT ACCOMMODATION
                                            if ((sub.key === "tour_accommodation" || sub.key === "event_accommodation") && !checked) {
                                              const otherAccommodationEnabled =
                                                (sub.key === "tour_accommodation" && formik.values.event_accommodation) ||
                                                (sub.key === "event_accommodation" && formik.values.tour_accommodation);

                                              if (!otherAccommodationEnabled) {
                                                formik.setFieldValue("accommodation", false);
                                                formik.setFieldValue("accommodation_add_on", false);
                                              }
                                            }
                                            if (
                                              (sub.key === "tour_product" ||
                                                sub.key === "event_product") &&
                                              !checked
                                            ) {
                                              const otherProductEnabled =
                                                (sub.key === "tour_product" &&
                                                  formik.values.event_product) ||
                                                (sub.key === "event_product" &&
                                                  formik.values.tour_product);

                                              if (!otherProductEnabled) {
                                                formik.setFieldValue("product_management", false);
                                                formik.setFieldValue("product_add_on", false);
                                                formik.setFieldValue("self_purchase", false);
                                              }
                                            }
                                          }}
                                        />
                                      </Box>

                                      {sub.description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                          {sub.description}
                                        </Typography>
                                      )}

                                      {/* Nested subcategories */}
                                      {formik.values[sub.key] && sub.subcategories?.map((nested) => (
                                        <Box key={nested.key} sx={{ ml: 3, mt: 1 }}>
                                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="body2">
                                              {nested.label}
                                            </Typography>

                                            <Checkbox
                                              checked={formik.values[nested.key]}
                                              onChange={(e) => {
                                                const checked = e.target.checked;
                                                formik.setFieldValue(nested.key, checked);

                                                if (nested.key === "tour_accommodation_add_on" || nested.key === "event_accommodation_add_on") {
                                                  if (checked) {
                                                    formik.setFieldValue("accommodation_add_on", true);
                                                  } else {
                                                    const otherAddOnEnabled =
                                                      (nested.key === "tour_accommodation_add_on" && formik.values.event_accommodation_add_on) ||
                                                      (nested.key === "event_accommodation_add_on" && formik.values.tour_accommodation_add_on);

                                                    if (!otherAddOnEnabled) {
                                                      formik.setFieldValue("accommodation_add_on", false);
                                                    }
                                                  }
                                                }
                                                if (
                                                  nested.key === "tour_sell_product_directly" ||
                                                  nested.key === "sell_product_directly"
                                                ) {
                                                  if (checked) {
                                                    formik.setFieldValue("self_purchase", true);
                                                  } else {
                                                    const otherEnabled =
                                                      (nested.key === "tour_sell_product_directly" &&
                                                        formik.values.sell_product_directly) ||
                                                      (nested.key === "sell_product_directly" &&
                                                        formik.values.tour_sell_product_directly);

                                                    if (!otherEnabled) {
                                                      formik.setFieldValue("self_purchase", false);
                                                    }
                                                  }
                                                }

                                                if (
                                                  nested.key === "tour_add_on_tour_event" ||
                                                  nested.key === "add_on_tour_event"
                                                ) {
                                                  if (checked) {
                                                    formik.setFieldValue("product_add_on", true);
                                                  } else {
                                                    const otherEnabled =
                                                      (nested.key === "tour_add_on_tour_event" &&
                                                        formik.values.add_on_tour_event) ||
                                                      (nested.key === "add_on_tour_event" &&
                                                        formik.values.tour_add_on_tour_event);

                                                    if (!otherEnabled) {
                                                      formik.setFieldValue("product_add_on", false);
                                                    }
                                                  }
                                                }


                                              }}
                                            />
                                          </Box>

                                          {nested.description && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                              {nested.description}
                                            </Typography>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                            {formik.touched[feature.key] && formik.errors[feature.key] && (
                              <Typography
                                color="error"
                                variant="caption"
                                sx={{ mt: 1, display: "block" }}
                              >
                                {formik.errors[feature.key]}
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ) : (
                        /* Fallback block for features without subcategories */
                        <Box
                          sx={{
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "grey.300",
                            bgcolor: "background.paper",
                            px: 2,
                            py: 1.5,
                            minHeight: 64,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            boxSizing: "border-box",
                          }}
                        >
                          <Typography fontWeight={600}>
                            {feature.label}
                          </Typography>

                          <Checkbox
                            checked={formik.values[feature.key]}
                            onChange={(e) => formik.setFieldValue(feature.key, e.target.checked)}
                          />
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>

            {/* Pricing Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <SectionHeader title="Pricing" />
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Monthly Price<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter monthly price"
                    name="monthly_price"
                    type="number"
                    size="small"
                    fullWidth
                    value={formik.values.monthly_price}
                    onChange={handleNumberChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.monthly_price &&
                      Boolean(formik.errors.monthly_price)
                    }
                    helperText={
                      formik.touched.monthly_price &&
                      formik.errors.monthly_price
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Yearly Price<span className="text-danger ms-1">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter yearly price"
                    name="yearly_price"
                    type="number"
                    size="small"
                    fullWidth
                    value={formik.values.yearly_price}
                    onChange={handleNumberChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.yearly_price &&
                      Boolean(formik.errors.yearly_price)
                    }
                    helperText={
                      formik.touched.yearly_price && formik.errors.yearly_price
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          </DialogContent>
          <DialogActions className="border-top p-7 d-flex justify-content-between">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-sm btn-outline btn-outline-primary"
              disabled={loading}
            >
              Cancel
            </button>

            <button type="submit" className="btn btn-sm btn-primary">
              {id ? "Update" : "Create"} Plan
              {loading && (
                <span
                  className="spinner-border spinner-border-sm ms-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
            </button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default AddEditTripolaPlan;